import type { ChangeMessage } from "./sse";

/** State of a connection (or of the whole client: the worst of its open connections). */
export type ConnectionStatus =
  | "idle" // nothing subscribed
  | "connecting"
  | "live"
  | "reconnecting"
  | "fallback" // 3+ failures in a row / refused — screens poll every 30 s
  | "paused" // hidden tab (> 5 min) / page left
  | "offline"; // the browser is offline

/** What a subscriber cares about: are changes of *its* topic arriving right now? */
export type TopicStatus = "connecting" | "live" | "fallback";

/**
 * Why a subscriber must refetch what it shows:
 * - `hub` — the stream lost track (replay impossible, slow client, reconnect without an id)
 * - `resume` — the tab came back (hidden > 5 min, offline, back-forward cache)
 * - `poll` — the topic is not live (fallback): the 30 s poll (design/07 §9.1 "Fallback")
 */
/**
 * Why a subscriber should re-read: `hub` (the hub asked / nothing to resume from), `resume` (back
 * from hidden / offline), `poll` (fallback clock), `subscribed` (the topic just went live — data
 * read before the subscribe may have missed a change, T13-40).
 */
export type ResyncReason = "hub" | "resume" | "poll" | "subscribed";

export interface Subscriber {
  onChange: (message: ChangeMessage) => void;
  onResync?: (reason: ResyncReason) => void;
  onStatus?: (status: TopicStatus) => void;
}

/** Bearer for signed-in apps; `force` = the last one was refused (refresh it first). */
export type TokenProvider = (force: boolean) => Promise<string | null> | string | null;

/** What a source (hub connection / per-service stream) gets from the client. */
export interface SourceHost {
  change(message: ChangeMessage): void;
  /** `null` = every topic of the source. */
  resync(topics: readonly string[] | null, reason: ResyncReason): void;
  /** A status changed — the client re-evaluates every subscriber. */
  statusChanged(): void;
  /** Current bearer (refreshed when {@link SourceHost.refuseToken} was called). */
  token(): Promise<string | null>;
  /** The bearer was refused: the next {@link SourceHost.token} forces a refresh. */
  refuseToken(): void;
  hasTokenProvider(): boolean;
  /** Token of the tracking link (`order:<number>` / `order.tracking:<number>`). */
  orderToken(): string | null;
  fetch: typeof fetch;
}

/** A connection kind the client routes topics to. */
export interface Source {
  readonly name: string;
  accepts(topic: string): boolean;
  /** First subscriber of `topic` arrived. */
  add(topic: string): void;
  /** Last subscriber of `topic` left. */
  remove(topic: string): void;
  status(): ConnectionStatus;
  topicStatus(topic: string): TopicStatus;
  pause(reason: "hidden" | "offline" | "pagehide"): void;
  /** Visible / online / page shown again: reconnect (and resync) if paused. */
  resume(): void;
  /** The app refreshed its token (hub: renew without reconnecting). */
  tokenChanged(): void;
  orderTokenChanged(): void;
  /** Topics the open connection carries (tests / debugging). */
  wireTopics(): string[];
  close(): void;
}

export interface RetryOptions {
  /** First reconnect delay (doubles up to {@link RetryOptions.maxRetryMs}). Default 1 s. */
  minRetryMs?: number;
  /** Default 30 s. */
  maxRetryMs?: number;
  /** Failures in a row before topics report `fallback`. Default 3. */
  fallbackAfter?: number;
  /** A stream silent this long is reopened. Default 45 s (hub pings every 20 s). */
  stallMs?: number;
  /** Topic changes within this window go out together. Default 50 ms. */
  batchMs?: number;
}

/** Builds a source for the client (`inventoryStream()`, `serviceStream(spec)` …). */
export type SourceFactory = (host: SourceHost, options: RetryOptions) => Source;

export function retryDelay(failures: number, min: number, max: number): number {
  return Math.min(min * 2 ** Math.max(0, failures - 1), max);
}
